// Hint finger basics (IMPLEMENTATION §2.1 fx/HintFinger.ts, UX §4 "Hint rules"): one pooled DOM pointer in
// #fx that demonstrates a tap (press pulses) or a drag (moves from → to), loops ui.json hints.fingerLoops
// times, then rests as a static pointer at hints.fingerIdleAlpha; it never takes input
// (pointer-events:none). M7 decides WHICH hint shows (priority rescue > scripted > contextual, re-pulse
// after hints.fingerRepulseMs idle); this module only draws it. Web Animations API, transform + opacity only.
// A tap can point DOWN (👇 above its tip) when the shell sees the upward finger would cover Sarge's line.

export interface HintFingerCfg {
  loops: number;
  idleAlpha: number;
  dragMs: number;
  pauseMs: number;
  /** Press pulse of a tap demo (juice.json events.hit.squash). */
  pressScale: number;
  /** Finger size in CSS px (the board cell, so it reads at every layout). */
  sizePx(): number;
}

export interface HintFinger {
  /** `down`: point down at (x, y) from above (the glyph sits above the tip). */
  tap(x: number, y: number, down?: boolean): void;
  drag(from: { x: number; y: number }, to: { x: number; y: number }): void;
  hide(): void;
  /** Diagnostics: what it shows. */
  state(): { kind: 'none' | 'tap' | 'drag'; looping: boolean };
}

export function createHintFinger(fxRoot: HTMLElement, cfg: HintFingerCfg): HintFinger {
  const doc = fxRoot.ownerDocument;
  const el = doc.createElement('div');
  el.className = 'gm-hint-finger';
  el.setAttribute('aria-hidden', 'true');
  el.textContent = '👆';
  el.style.cssText =
    'position:absolute;left:0;top:0;display:none;pointer-events:none;line-height:1;' +
    'will-change:transform,opacity;filter:drop-shadow(0 2px 3px rgba(0,0,0,.5));';
  fxRoot.appendChild(el);
  let anim: Animation | null = null;
  let kind: 'none' | 'tap' | 'drag' = 'none';
  let looping = false;

  const stop = (): void => {
    anim?.cancel();
    anim = null;
    looping = false;
  };
  let down = false;
  const glyph = (d: boolean): void => {
    down = d;
    el.textContent = d ? '👇' : '👆';
  };
  const at = (x: number, y: number, s = 1): string => {
    const size = cfg.sizePx();
    // the fingertip (top centre of the glyph; bottom centre when pointing down) sits on (x, y)
    return `translate3d(${x - size / 2}px,${down ? y - size : y}px,0) scale(${s})`;
  };
  const start = (frames: Keyframe[], x: number, y: number): void => {
    stop();
    const size = cfg.sizePx();
    el.style.fontSize = `${size}px`;
    el.style.display = 'block';
    el.style.opacity = '1';
    el.style.transform = at(x, y);
    if (typeof el.animate !== 'function') return; // no Web Animations: a static pointer
    looping = true;
    anim = el.animate(frames, {
      duration: cfg.dragMs + cfg.pauseMs,
      iterations: cfg.loops,
      easing: 'ease-in-out',
    });
    anim.onfinish = () => {
      looping = false;
      el.style.opacity = String(cfg.idleAlpha);
    };
  };

  return {
    tap(x, y, d = false) {
      kind = 'tap';
      glyph(d);
      start(
        [
          { transform: at(x, y), opacity: 1 },
          { transform: at(x, y, cfg.pressScale), opacity: 1 },
          { transform: at(x, y), opacity: 1 },
        ],
        x,
        y,
      );
    },
    drag(from, to) {
      kind = 'drag';
      glyph(false);
      start(
        [
          { transform: at(from.x, from.y), opacity: 1 },
          { transform: at(to.x, to.y), opacity: 1 },
          { transform: at(to.x, to.y), opacity: 0 },
        ],
        from.x,
        from.y,
      );
    },
    hide() {
      stop();
      kind = 'none';
      el.style.display = 'none';
    },
    state: () => ({ kind, looping }),
  };
}
